import * as React from 'react';

import { normalizeRoomCodeInput } from '@flowshare/shared';
import { cn } from '@/lib/utils';

interface RoomCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  size?: 'default' | 'large';
  className?: string;
  label?: string;
}

export function RoomCodeInput({
  value,
  onChange,
  onComplete,
  autoFocus = false,
  disabled = false,
  size = 'default',
  className,
  label = 'Room code',
}: RoomCodeInputProps) {
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const activeIndex = Math.min(value.length, 5);

  const handleChange = (raw: string) => {
    const next = normalizeRoomCodeInput(raw);
    onChange(next);
  };

  return (
    <div
      className={cn(
        'relative grid w-full select-none grid-cols-6 gap-1.5 sm:gap-2',
        size === 'large' ? 'max-w-[360px]' : 'max-w-[280px]',
        className,
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {Array.from({ length: 6 }).map((_, index) => {
        const char = value[index];
        const isActive = focused && index === activeIndex && !disabled;
        return (
          <div
            key={index}
            className={cn(
              'code-cell relative grid aspect-square min-w-0 place-items-center rounded-xl font-semibold uppercase transition-all duration-150',
              size === 'large' ? 'text-xl sm:text-2xl' : 'text-base sm:text-lg',
              char ? 'text-foreground' : 'text-muted-foreground/40',
              isActive && 'border-primary/60 ring-2 ring-ring',
              disabled && 'opacity-60',
            )}
          >
            {char ?? (
              <span
                className={cn(
                  'size-1.5 rounded-full bg-muted-foreground/20',
                  isActive && 'bg-primary/50',
                )}
              />
            )}
            {isActive && char ? (
              <span className="absolute right-1.5 top-1.5 h-3 w-px animate-pulse bg-primary/70" />
            ) : null}
          </div>
        );
      })}

      {focused ? (
        <span className="pointer-events-none absolute -bottom-5 left-0 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/60">
          {label}
        </span>
      ) : null}

      <input
        ref={inputRef}
        value={value}
        onChange={(event) => handleChange(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && value.length === 6) onComplete?.(value);
        }}
        maxLength={32}
        autoFocus={autoFocus}
        disabled={disabled}
        autoComplete="off"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        inputMode="text"
        aria-label={label}
        className="absolute inset-0 cursor-text opacity-0 disabled:cursor-not-allowed"
      />
    </div>
  );
}
